import type { PrescriptionChoice } from "@care-by-ella/contracts";

// TES-1291 (TES-1267 §4) — the monthly review's decision as a structured
// outcome, in the same sections as every other treatment decision.
// A-02/09C §4:
//   "Samma adminflöde används vid … medicinsk månadsuppföljning,
//    receptförnyelse, dosjustering …"
//
// Each outcome is what one of the review's buttons already did, with the
// same requests in the same order, so the server side is unchanged:
//   - renew:       the comment, then the renewal (the old primary button);
//   - follow_up:   the comment only (was "Godkänn och publicera");
//   - dose_change: the comment, then the new order on the current period
//                  (was "Godkänn och publicera ny dos", which sent no
//                  comment and asked for no Webdoc tick);
//   - reject:      the rejection with its reason (was "Avslå").
// Pause, end and medication switch are the decision workspace's outcomes
// ("Behandlingsbeslut"), not the review's.
export const MONTHLY_OUTCOMES = [
  "renew",
  "follow_up",
  "dose_change",
  "reject",
] as const;
export type MonthlyOutcome = (typeof MONTHLY_OUTCOMES)[number];

// "Receptförnyelse", "Dosjustering" and "Medicinsk månadsuppföljning" are
// A-02's words; the rejection's label is ours.
export const MONTHLY_OUTCOME_LABEL: Record<MonthlyOutcome, string> = {
  renew: "Receptförnyelse",
  follow_up: "Medicinsk månadsuppföljning utan förnyelse",
  dose_change: "Dosjustering",
  reject: "Avslå förnyelsen",
};

/** Which parts of the form the outcome needs. */
export function monthlyOutcomeNeeds(outcome: MonthlyOutcome): {
  order: boolean;
  comment: boolean;
  prescription: boolean;
  phase: boolean;
} {
  switch (outcome) {
    case "renew":
      return { order: true, comment: true, prescription: true, phase: true };
    case "dose_change":
      return { order: true, comment: true, prescription: false, phase: true };
    case "follow_up":
      return { order: false, comment: true, prescription: false, phase: true };
    case "reject":
      // The rejection publishes no comment (it never did), so it carries no
      // phase decision either; the server refuses it while a keep-wish is
      // open (refuseOpenKeepWish).
      return {
        order: false,
        comment: false,
        prescription: false,
        phase: false,
      };
  }
}

export interface MonthlyDecisionState {
  outcome: MonthlyOutcome | null;
  /** renewalWebdocBlocker: the note compiled, copied and ticked. */
  webdocBlocker: string | null;
  orderReady: boolean;
  commentGiven: boolean;
  prescriptionChosen: boolean;
  /** phaseDecisionMissing: set while her keep-wish waits for a phase. */
  phaseMissing: string | null;
  keepWishOpen: boolean;
  /** TES-1431 — the prescription choice under Recept, null until chosen. */
  prescriptionChoice?: PrescriptionChoice | null;
  /** TES-1431 — her due F-05 is unsubmitted (renewalReview.monthlyCheckinMissing). */
  monthlyCheckinMissing?: boolean;
}

// TES-1431 (TES-1425 §6) — "F-05 … måste vara genomförd innan läkaren kan
// fatta nästa receptbeslut". The server refuses the renewal with the same
// rule (PrescriptionCycleService.approveRenewal); saying so here keeps the
// comment from being published ahead of a renewal that would then be
// refused. "Recept kan inte utfärdas ännu" and the other outcomes stay open.
// Words: Ö-790 (2), Emelie 2026-10-21, verbatim.
export const MONTHLY_CHECKIN_MISSING_BLOCKER =
  "Månadsincheckningen (F-05) saknas. Underlaget behöver kompletteras inför bedömning av receptförnyelse. Välj ’Recept kan inte utfärdas ännu’ eller annat tillämpligt utfall.";

/**
 * What still stands in the way of publishing, or null. The checks and their
 * words are the old buttons' own, in the same order.
 */
export function monthlyDecisionBlocker(s: MonthlyDecisionState): string | null {
  if (s.outcome === null) return "Välj ett utfall.";
  if (
    s.outcome === "renew" &&
    s.monthlyCheckinMissing &&
    s.prescriptionChoice !== "not_yet"
  )
    return MONTHLY_CHECKIN_MISSING_BLOCKER;
  if (s.webdocBlocker) return s.webdocBlocker;
  const needs = monthlyOutcomeNeeds(s.outcome);
  if (needs.order && !s.orderReady) return "Fyll i läkemedel, dos och frekvens";
  if (needs.comment && !s.commentGiven)
    return "Skriv kommentaren till patienten först";
  if (needs.prescription && !s.prescriptionChosen)
    return "Välj receptstatus i Webdoc";
  if (needs.phase && s.phaseMissing) return s.phaseMissing;
  if (s.outcome === "reject" && s.keepWishOpen)
    return "Patientens önskemål om att behålla vikten kräver ett fasbeslut, som publiceras med återkopplingen";
  return null;
}

export interface MonthlyOutcomeEffects {
  dinBehandling: string;
  hem: string;
  comment: string;
}

const COMMENT_PUBLISHED =
  "Publiceras under Medicinska uppföljningar i appen, med fasbeslutet om ett är valt.";

/**
 * Before publishing, what the outcome changes for her (A-02 §7). Each line
 * describes what the server does today (PrescriptionCycleService.approve,
 * adjustDose, rejectRenewal and publishMonthlyReview, and Hem's
 * programChange), not a promise.
 */
export function monthlyOutcomeEffects(
  outcome: MonthlyOutcome,
  prescription: PrescriptionChoice | null,
): MonthlyOutcomeEffects {
  switch (outcome) {
    case "renew":
      if (prescription === "not_yet") {
        return {
          dinBehandling:
            "Ingen ny receptperiod skapas. Granskningen står kvar öppen tills receptet kan utfärdas.",
          hem: "Ingen ändring av planen visas.",
          comment: COMMENT_PUBLISHED,
        };
      }
      return {
        dinBehandling:
          prescription === "existing_valid"
            ? "En ny receptperiod startar med ordinationen under Recept, på det recept hon redan har."
            : "En ny receptperiod startar med ordinationen under Recept.",
        hem:
          prescription === "existing_valid"
            ? "Hon får notisen ”Din behandling fortsätter”."
            : "”Ditt recept har förnyats” visas i fem dagar.",
        comment: COMMENT_PUBLISHED,
      };
    case "dose_change":
      return {
        dinBehandling:
          "Den aktuella receptperioden får ordinationen under Recept. Ingen ny receptperiod skapas.",
        hem: "”Din dos har justerats – se din plan” visas i fem dagar.",
        comment: COMMENT_PUBLISHED,
      };
    case "follow_up":
      return {
        dinBehandling:
          "Ordinationen och receptperioden ändras inte. Förnyelsen står kvar att avgöra.",
        hem: "Ingen ändring av planen visas.",
        comment: COMMENT_PUBLISHED,
      };
    case "reject":
      return {
        dinBehandling:
          "Förnyelsen avslås och behandlingen avslutas. Ingen ny receptperiod skapas. Abonnemanget sägs inte upp automatiskt.",
        hem: "Ingen ny receptperiod visas.",
        comment: "Ingen kommentar publiceras vid avslag.",
      };
  }
}
